/**
 * Pure helpers backing the POS "Printer Profiles" editor
 * ({@link ../screens/PrinterProfilesScreen} / {@link ../screens/PrinterProfileEditScreen}).
 *
 * A "printer profile" is the backend `printerEvent` model
 * (`{ name, restaurant, place, type, products[], copy }`). These helpers are kept
 * free of React/IO so they can be unit-tested (see app/tests/printerProfiles.test.js)
 * and so the printers-preservation contract below is explicit and regression-guarded.
 */

/**
 * Resolve a printer event's identity the same way the pairing screen does
 * ({@link ../screens/SettingsPrinterPairingScreen}) — Mongo `_id` first, then a
 * client-side `id` fallback.
 *
 * @param {object} event a printerEvent
 * @returns {string|undefined} the event id, or undefined when absent
 */
export const getEventId = (event) => event?._id || event?.id;

/**
 * Merge a freshly created/updated printer event back into the local
 * `printerEvents` array while PRESERVING that event's local `printers` device
 * pairing.
 *
 * `printers` is a local-only field (the physical device(s) a profile prints to,
 * set on {@link ../screens/SettingsPrinterPairingScreen}); the server never
 * returns it. If we overwrote the event with the raw server response we would
 * silently drop the pairing, so it is re-attached here.
 *
 * Matching mirrors the pairing screen's `updatePrinterEvents`: by id first, then
 * by `type` + `name` (covers a freshly created event whose server id differs from
 * any temporary local id). The result is ALWAYS non-empty when either input has
 * content, because {@link ../helpers/SystemContext} refuses to persist an empty
 * `printerEvents` array.
 *
 * @param {object} saved the printer event returned from create/update (or the
 *   locally-built body when the endpoint returns no payload)
 * @param {object[]} [existing] the current `printerEvents` from SystemContext
 * @returns {object[]} the next `printerEvents` array
 */
export const mergeSavedEvent = (saved, existing = []) => {
    const list = Array.isArray(existing) ? existing : [];
    if (!saved) {
        return list;
    }

    const id = getEventId(saved);
    let idx = id ? list.findIndex((e) => getEventId(e) === id) : -1;
    // Fallback for a just-created event whose server id differs from any temp id.
    if (idx === -1 && saved.type && saved.name) {
        idx = list.findIndex((e) => e.type === saved.type && e.name === saved.name);
    }

    const localPrinters = idx >= 0 && Array.isArray(list[idx].printers) ? list[idx].printers : [];
    const merged = { ...saved, printers: localPrinters };

    if (idx >= 0) {
        const next = list.slice();
        next[idx] = merged;

        return next;
    }

    return [...list, merged];
};

/**
 * Narrow the product catalog for the profile's item picker: only sellable foods
 * (visible + enabled, matching the SaleScreen convention), optionally scoped to a
 * category and/or a fuzzy keyword search.
 *
 * Search reuses `searchFoodByKeywords` from PairingModal (diacritics-insensitive,
 * HU-name based); foods lacking a Hungarian name string are excluded from the
 * search pass so the reused helper never throws on `food.name['hu']`. That module
 * is required lazily (only when a query is present) so the routing helpers below
 * — imported by app/helpers/BackgroundTasks.js — never pull PairingModal's
 * React-Native/context import chain into the background-print graph.
 *
 * @param {object} params
 * @param {object[]} params.foods the SystemContext `foods` catalog
 * @param {string|null} [params.categoryId] restrict to `food.category === categoryId`
 * @param {string} [params.query] fuzzy keyword search over the food name
 * @returns {object[]} the filtered foods
 */
export const filterFoodsForPicker = ({ foods = [], categoryId = null, query = '' } = {}) => {
    const source = Array.isArray(foods) ? foods : [];
    let list = source.filter((food) => food?.visible === true && food?.enabled === true);

    if (categoryId) {
        list = list.filter((food) => food.category === categoryId);
    }

    const trimmed = (query || '').trim();
    if (trimmed.length > 0) {
        // Lazy require: keeps PairingModal's RN/context chain out of BackgroundTasks.
        const {searchFoodByKeywords} = require('../components/PairingModal');
        const searchable = list.filter((food) => food?.name && typeof food.name.hu === 'string');
        list = searchFoodByKeywords(trimmed, searchable);
    }

    return list;
};

/**
 * Human-friendly resolver for a food's display name from its locale map
 * (`{ hu, en, ... }`). Never returns the raw object.
 *
 * @param {object} food a food/product
 * @returns {string} the best available name (HU → EN → empty string)
 */
export const foodName = (food) => food?.name?.hu || food?.name?.en || '';

/**
 * Printer event types that route by assigned products. `receipt`/`cashRegister`
 * profiles print the whole order, so the product picker is not applicable to them
 * (see the print routing in app/helpers/BackgroundTasks.js).
 */
export const PRODUCT_ROUTED_TYPES = ['terminal', 'label'];

/**
 * Whether a given profile type uses the per-product routing (and therefore the
 * item picker), as opposed to printing the entire order.
 *
 * @param {string} type the profile `type`
 * @returns {boolean}
 */
export const isProductRoutedType = (type) => PRODUCT_ROUTED_TYPES.includes(type);

/**
 * Whether a profile is flagged as the store's "catch-all" — the profile that
 * also prints any product assigned to no other terminal profile (so newly
 * created foods still print somewhere without being added to every profile).
 *
 * @param {object} event a printerEvent
 * @returns {boolean}
 */
export const isCatchAllEvent = (event) => event?.catchAll === true;

/**
 * Collect the set of product ids that are EXPLICITLY assigned to at least one
 * terminal profile. Only profiles with a non-empty `products` list contribute:
 * an empty-list profile is a legacy "print everything" profile and covers no
 * product in particular, so it must not suppress the catch-all fallback.
 *
 * The returned set defines which products are "already routed"; anything outside
 * it is considered unassigned/new and is what a catch-all profile picks up.
 *
 * @param {object[]} terminalEvents the `type === 'terminal'` printerEvents
 * @param {(value:any)=>string} [normalize] id normalizer (defaults to identity);
 *   BackgroundTasks passes its ObjectId-aware normalizer here
 * @returns {Set<string>} the assigned product ids
 */
export const collectAssignedTerminalProductIds = (terminalEvents = [], normalize = (v) => v) => {
    const ids = new Set();
    (Array.isArray(terminalEvents) ? terminalEvents : []).forEach((event) => {
        const products = Array.isArray(event?.products) ? event.products : [];
        if (products.length === 0) {
            return;
        }
        products.map(normalize).filter(Boolean).forEach((id) => ids.add(id));
    });

    return ids;
};

/**
 * Decide whether a single terminal profile should print a given product. Central
 * routing rule backing both kitchen-print paths in app/helpers/BackgroundTasks.js:
 *
 *  1. Empty `products` list  → legacy "print everything" profile (prints all).
 *  2. Product is in the list → explicitly assigned to this profile (prints).
 *  3. Catch-all profile AND the product is assigned to no terminal profile at all
 *     → the profile picks up the unassigned/new item (prints).
 *
 * Rule 3 is what makes a newly created food print without being hand-added to a
 * profile: the store designates one catch-all terminal and every new product
 * lands there. The behaviour is purely additive — it only ever prints items that
 * would otherwise be silently dropped.
 *
 * @param {object} event a terminal printerEvent { products, catchAll }
 * @param {string} productId the order item's (already-normalized) product id
 * @param {Set<string>} assignedProductIds output of {@link collectAssignedTerminalProductIds}
 * @param {(value:any)=>string} [normalize] id normalizer for `event.products`
 * @returns {boolean} whether this profile should print the product
 */
export const terminalEventPrintsProduct = (
    event,
    productId,
    assignedProductIds,
    normalize = (v) => v,
) => {
    const products = Array.isArray(event?.products) ? event.products.map(normalize).filter(Boolean) : [];
    if (products.length === 0) {
        return true;
    }

    const pid = normalize(productId);
    if (pid && products.includes(pid)) {
        return true;
    }

    const assigned = assignedProductIds instanceof Set ? assignedProductIds : new Set();

    return isCatchAllEvent(event) && Boolean(pid) && !assigned.has(pid);
};
